import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { FaSearch, FaMapMarkerAlt, FaTimes, FaPlus, FaHistory } from "react-icons/fa";
import { apiClient } from "../../services/api/apiService";
import "../../styles/components/VenueSearch.css";

// ── Provider configuration ────────────────────────────────────────────────────
//
// ACTIVE: Geoapify  — 3 000 req/day free, no credit card required
//   Sign up at https://www.geoapify.com/ → API Keys → create key
//   Add REACT_APP_GEOAPIFY_API_KEY=<key> to frontend/.env
//
// DISABLED: Google Maps Places API (ready to swap in)
//   To switch: comment the GEOAPIFY block, uncomment the GOOGLE block below,
//   set REACT_APP_GOOGLE_MAPS_API_KEY in .env, and enable Maps JS + Places API
//   on your Google Cloud project.
//
// ── GEOAPIFY (active) ─────────────────────────────────────────────────────────

// Minimum characters before firing the Geoapify API (saves quota + improves results)
const MIN_SEARCH_CHARS = 3;

// ── GOOGLE MAPS (disabled — uncomment to activate) ───────────────────────────
// const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// function loadMapsScript(apiKey, onLoad) {
//   if (window.google?.maps?.places) { onLoad(); return; }
//   if (document.getElementById("__sportifide_gmaps__")) {
//     const poll = setInterval(() => { if (window.google?.maps?.places) { clearInterval(poll); onLoad(); } }, 150);
//     return;
//   }
//   const s = document.createElement("script");
//   s.id = "__sportifide_gmaps__";
//   s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
//   s.async = true; s.defer = true; s.onload = onLoad;
//   document.head.appendChild(s);
// }
// ─────────────────────────────────────────────────────────────────────────────

/**
 * VenueSearch — reusable venue picker.
 * Active provider: Geoapify Geocoding Autocomplete API (no credit card, 3k/day free).
 * Falls back to a plain text input when REACT_APP_GEOAPIFY_API_KEY is not set.
 *
 * Props:
 *   value      { venue, venue_place_id, venue_address, venue_lat, venue_lng, court }
 *   onChange   fn(venueObject)   — called with the full venue object on every change
 *   label      string            (default "Venue")
 *   required   bool
 *   className  string            — extra class added to the outer wrapper
 */
const VenueSearch = ({
  value = {},
  onChange,
  label = "Venue",
  required = false,
  className = "",
  hideCourt = false,
}) => {
  const [searchText, setSearchText] = useState(value.venue || "");
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // True when the last search finished with zero places (BUG-50)
  const [noResults, setNoResults] = useState(false);
  const [isSelected, setIsSelected] = useState(!!value.venue_place_id);
  const [isCustom, setIsCustom] = useState(
    !!(value.venue && !value.venue_place_id)
  );
  // Viewport-relative position for the portal dropdown
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  // Fallback state
  const [apiError, setApiError] = useState(false);
  const [savedVenues, setSavedVenues] = useState(null); // null = not fetched yet
  const [savedLoading, setSavedLoading] = useState(false);

  const debounce = useRef(null);
  const abortCtrl = useRef(null);
  const containerRef = useRef(null);
  const inputWrapRef = useRef(null);
  // Cache up to 5 recent Geoapify responses to avoid redundant API calls
  const searchCache = useRef(new Map());

  // Read the API key at render time so tests can override process.env per-test
  const noApiKey = !process.env.REACT_APP_GEOAPIFY_API_KEY;
  const inFallback = apiError || noApiKey;

  // ── Fetch saved venues when entering fallback mode ────────────────────────
  useEffect(() => {
    if (!inFallback || savedVenues !== null || savedLoading) return;
    setSavedLoading(true);
    apiClient.get("/matches/venues/suggestions")
      .then((res) => setSavedVenues(res.data.venues || []))
      .catch(() => setSavedVenues([]))
      .finally(() => setSavedLoading(false));
  }, [inFallback, savedVenues, savedLoading]);

  // ── Close dropdown on outside click or scroll ──────────────────────────
  useEffect(() => {
    const closeDropdown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        if (!isSelected && !isCustom) setSearchText(value.venue || "");
      }
    };
    const closeOnScroll = () => setIsOpen(false);
    document.addEventListener("mousedown", closeDropdown);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeDropdown);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [isSelected, isCustom, value.venue]);

  const emit = useCallback((venueData) => onChange(venueData), [onChange]);

  // ── Recalculate dropdown anchor (call before opening) ──────────────────
  const calcDropdownPos = useCallback(() => {
    if (inputWrapRef.current) {
      const r = inputWrapRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }, []);

  // ── Geoapify fetch (with simple LRU-style cache for the last 5 queries) ──
  const fetchPredictions = useCallback(
    (q) => {
      // Return cached result immediately — no API call needed
      if (searchCache.current.has(q)) {
        const cached = searchCache.current.get(q);
        setPredictions(cached);
        setNoResults(cached.length === 0);
        calcDropdownPos();
        setIsOpen(true);
        setIsLoading(false);
        return;
      }

      if (abortCtrl.current) abortCtrl.current.abort();
      const ctrl = new AbortController();
      abortCtrl.current = ctrl;
      // Never spin forever: give up after 10 s and show "No places found"
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        ctrl.abort();
      }, 10000);

      const url =
        `https://api.geoapify.com/v1/geocode/autocomplete` +
        `?text=${encodeURIComponent(q)}` +
        `&limit=7` +
        `&apiKey=${process.env.REACT_APP_GEOAPIFY_API_KEY}`;

      setIsLoading(true);
      setNoResults(false);
      fetch(url, { signal: ctrl.signal })
        .then((r) => r.json())
        .then((data) => {
          clearTimeout(timeoutId);
          setIsLoading(false);
          const features = data?.features ?? [];
          // Store in cache (cap at 5 entries — evict oldest first)
          if (searchCache.current.size >= 5) {
            searchCache.current.delete(searchCache.current.keys().next().value);
          }
          searchCache.current.set(q, features);
          calcDropdownPos();
          setPredictions(features);
          setNoResults(features.length === 0);
          setIsOpen(true);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          if (err.name !== "AbortError") {
            setIsLoading(false);
            setApiError(true); // switch to saved-venues fallback
          } else if (timedOut) {
            setIsLoading(false);
            setPredictions([]);
            setNoResults(true);
            calcDropdownPos();
            setIsOpen(true);
          }
          // Otherwise a newer search (or a cleared box) replaced this one
        });
    },
    [calcDropdownPos]
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchText(q);
    setIsSelected(false);
    setNoResults(false);

    if (!q.trim()) {
      clearTimeout(debounce.current);
      if (abortCtrl.current) abortCtrl.current.abort();
      setIsLoading(false);
      setPredictions([]);
      // In fallback, show all saved venues on empty query
      if (inFallback && savedVenues && savedVenues.length > 0) {
        calcDropdownPos();
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
      emit({ venue: "", venue_place_id: "", venue_address: "", venue_lat: null, venue_lng: null, court: value.court || "" });
      return;
    }

    if (inFallback) {
      // Client-side filter against saved venues — no API call needed
      calcDropdownPos();
      setIsOpen(true);
      return;
    }

    // Minimum character guard — don't fire the API for very short queries
    if (q.trim().length < MIN_SEARCH_CHARS) {
      clearTimeout(debounce.current);
      if (abortCtrl.current) abortCtrl.current.abort();
      setIsLoading(false);
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPredictions(q), 400);
  };

  const handleSelectPrediction = (feature) => {
    const p = feature.properties;
    // Prefer explicit place name; fall back gracefully
    const name = p.name || p.address_line1 || p.formatted?.split(",")[0] || "";
    const address = p.formatted || "";
    setSearchText(name);
    setIsSelected(true);
    setIsOpen(false);
    setPredictions([]);
    emit({
      venue: name,
      venue_place_id: p.place_id || "",
      venue_address: address,
      venue_lat: p.lat ?? null,
      venue_lng: p.lon ?? null,
      court: value.court || "",
    });
  };

  // Select a venue from saved-venues fallback list
  const handleSelectSavedVenue = (sv) => {
    setSearchText(sv.venue);
    setIsSelected(true);
    setIsOpen(false);
    emit({
      venue: sv.venue,
      venue_place_id: sv.place_id || "",
      venue_address: sv.venue_address || "",
      venue_lat: sv.venue_lat ?? null,
      venue_lng: sv.venue_lng ?? null,
      court: value.court || "",
    });
  };

  const handleClear = () => {
    setSearchText("");
    setIsSelected(false);
    setIsCustom(false);
    setPredictions([]);
    setIsOpen(false);
    emit({ venue: "", venue_place_id: "", venue_address: "", venue_lat: null, venue_lng: null, court: value.court || "" });
  };

  const handleCustomMode = () => {
    setIsCustom(true);
    setIsOpen(false);
    setIsSelected(true);
    emit({ venue: searchText, venue_place_id: "", venue_address: "", venue_lat: null, venue_lng: null, court: value.court || "" });
  };

  const handleCustomInput = (e) => {
    const v = e.target.value;
    setSearchText(v);
    emit({ venue: v, venue_place_id: "", venue_address: "", venue_lat: null, venue_lng: null, court: value.court || "" });
  };

  const handleCourtChange = (e) => emit({ ...value, court: e.target.value });

  // Clean address hint: strip "Name, " prefix to avoid repetition
  const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const addressHint =
    value.venue_address && value.venue
      ? value.venue_address.replace(new RegExp(`^${escRe(value.venue)},?\\s*`), "")
      : value.venue_address;

  const showCourtField = isSelected || isCustom;

  // ── Filtered saved venues for fallback dropdown ──────────────────────────
  const filteredSaved = inFallback && savedVenues
    ? searchText.trim()
      ? savedVenues.filter((v) =>
          v.venue.toLowerCase().includes(searchText.trim().toLowerCase())
        )
      : savedVenues.slice(0, 8)
    : [];

  // ── Portal dropdown ──────────────────────────────────────────────────────
  // Rendered at document.body to escape the FloatingCard's stacking context.
  // FloatingCard uses backdrop-filter which creates a new CSS stacking context,
  // trapping z-index. A portal bypasses this entirely.
  // Fallback dropdown (saved venues from DB)
  const fallbackDropdown =
    isOpen &&
    inFallback &&
    ReactDOM.createPortal(
      <ul
        className="vns-dropdown vns-dropdown--portal"
        role="listbox"
        style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
      >
        <li className="vns-fallback-banner" role="presentation">
          <span>📍 Showing previously used venues</span>
        </li>
        {savedLoading && (
          <li className="vns-fallback-loading" role="presentation">Loading saved venues…</li>
        )}
        {!savedLoading && filteredSaved.length === 0 && (
          <li className="vns-fallback-empty" role="presentation">No matching saved venues</li>
        )}
        {filteredSaved.map((sv) => (
          <li key={sv.place_id || sv.venue} role="option">
            <button
              type="button"
              className="vns-option"
              onMouseDown={() => handleSelectSavedVenue(sv)}
            >
              <FaHistory className="vns-option-icon vns-option-icon--saved" />
              <div className="vns-option-text">
                <span className="vns-option-main">{sv.venue}</span>
                {sv.venue_address && (
                  <span className="vns-option-secondary">{sv.venue_address}</span>
                )}
              </div>
            </button>
          </li>
        ))}
        <li role="option">
          <button
            type="button"
            className="vns-option vns-option--custom"
            onMouseDown={handleCustomMode}
          >
            <FaPlus className="vns-option-icon" />
            <span>Can't find it? Enter manually</span>
          </button>
        </li>
      </ul>,
      document.body
    );

  const portalDropdown =
    isOpen &&
    !inFallback &&
    (predictions.length > 0 || noResults) &&
    ReactDOM.createPortal(
      <ul
        className="vns-dropdown vns-dropdown--portal"
        role="listbox"
        style={{
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}
      >
        {noResults && predictions.length === 0 && (
          <li className="vns-fallback-empty" role="presentation" data-testid="venue-no-results">
            No places found
          </li>
        )}
        {predictions.map((f) => {
          const p = f.properties;
          const mainText =
            p.name || p.address_line1 || p.formatted?.split(",")[0] || "";
          const secondary = p.formatted
            ? p.name
              ? p.formatted.replace(new RegExp(`^${escRe(p.name)},?\\s*`), "")
              : p.formatted
            : "";
          return (
            <li key={p.place_id || p.formatted} role="option">
              <button
                type="button"
                className="vns-option"
                onMouseDown={() => handleSelectPrediction(f)}
              >
                <FaMapMarkerAlt className="vns-option-icon" />
                <div className="vns-option-text">
                  <span className="vns-option-main">{mainText}</span>
                  {secondary && (
                    <span className="vns-option-secondary">{secondary}</span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
        <li role="option">
          <button
            type="button"
            className="vns-option vns-option--custom"
            onMouseDown={handleCustomMode}
          >
            <FaPlus className="vns-option-icon" />
            <span>Can't find it? Add a custom location</span>
          </button>
        </li>
      </ul>,
      document.body
    );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`vns-wrapper ${className}`} ref={containerRef}>
      {/* Portal dropdowns are injected at document.body, outside this tree */}
      {portalDropdown}
      {fallbackDropdown}

      <div className="vns-fields">
        {/* ── Location search ── */}
        <div className="vns-main">
          <label className="form-label-gaming">
            📍 {label}
            {required && <span className="vns-required"> *</span>}
          </label>

          {/* ── Plain text (custom entry mode) ── */}
          {isCustom ? (
            <div className="vns-input-wrap" ref={inputWrapRef}>
              <FaMapMarkerAlt className="vns-icon vns-icon--pin" />
              <input
                type="text"
                value={searchText}
                onChange={handleCustomInput}
                placeholder="Enter venue name or address"
                className="form-input-gaming vns-input"
              />
              {searchText && (
                <button type="button" className="vns-clear" onClick={handleClear} title="Clear venue">
                  <FaTimes />
                </button>
              )}
            </div>
          ) : (
            /* ── Geoapify / fallback search ── */
            <div className="vns-input-wrap" ref={inputWrapRef}>
              {isSelected ? (
                <FaMapMarkerAlt className="vns-icon vns-icon--pin vns-icon--selected" />
              ) : (
                <FaSearch className="vns-icon" />
              )}
              <input
                type="text"
                value={searchText}
                onChange={handleSearchInput}
                onFocus={() => {
                  calcDropdownPos();
                  if (inFallback) {
                    // Always open fallback dropdown on focus
                    setIsOpen(true);
                  } else if (predictions.length > 0) {
                    setIsOpen(true);
                  }
                }}
                placeholder={inFallback ? "Search saved venues or enter manually..." : "Search park, school, gymnasium..."}
                className={`form-input-gaming vns-input${isSelected ? " vns-input--selected" : ""}`}
                autoComplete="off"
              />
              {isLoading && <span className="vns-spinner" role="status" aria-label="Searching places" />}
              {searchText && !isLoading && (
                <button type="button" className="vns-clear" onClick={handleClear} title="Clear venue">
                  <FaTimes />
                </button>
              )}
            </div>
          )}

          {/* Address hint after selection */}
          {isSelected && addressHint && (
            <p className="vns-address-hint">
              <FaMapMarkerAlt className="vns-address-icon" />
              {addressHint}
            </p>
          )}
        </div>

        {/* ── Court / Field # (appears once a venue is picked) ── */}
        {showCourtField && !hideCourt && (
          <div className="vns-court">
            <label className="form-label-gaming">
              Court / Field #
              <span className="vns-optional"> (Optional)</span>
            </label>
            <input
              type="text"
              value={value.court || ""}
              onChange={handleCourtChange}
              placeholder="e.g. Court 4, North Side"
              className="form-input-gaming"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueSearch;
